// components/admin/UserTable.tsx
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { Edit, LockOpen, Lock, Settings, Add } from "@mui/icons-material";
import { User } from "../../context/types";
import { WorkspaceLimitsModal } from "./WorkspaceLimitsModal";
import {
  addPlanToWorkspace,
  getAllPlans,
  updateWorkspacePlanAndCredits,
  revokeFullAccess,
} from "../../client/api";
import { AddPlanModal } from "./AddPlanModal";

interface UserTableProps {
  users: User[];
  onEdit: (user: User) => void;
  onGrantAccess: (user: User) => void;
  onRevokeAccess: (user: User) => void;
  onUpdateLimits: (workspaceId: string, limits: any) => Promise<void>;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onEdit,
  onGrantAccess,
  onRevokeAccess,
  onUpdateLimits,
}) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
  const [limitsModalOpen, setLimitsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [addPlanModalOpen, setAddPlanModalOpen] = useState(false);
  const [workspacesModalOpen, setWorkspacesModalOpen] = useState(false);
  const [selectedUserWorkspaces, setSelectedUserWorkspaces] =
    useState<User | null>(null);

  const handleEditWorkspaces = (user: User) => {
    setSelectedWorkspace(user.ownedWorkspaces?.[0]);
    setSelectedUserWorkspaces(user);
    setWorkspacesModalOpen(true);
  };

  const handleEditLimits = (workspace: any) => {
    setSelectedWorkspace(workspace);
    setLimitsModalOpen(true);
  };

  const handleSubmitLimits = async (limits: any) => {
    if (selectedWorkspace) {
      await onUpdateLimits(selectedWorkspace._id, limits);
      setLimitsModalOpen(false);
      setSelectedWorkspace(null);
    }
  };

  const handleAddPlanClick = (user: User) => {
    setSelectedUser(user);
    setAddPlanModalOpen(true);
  };

  const handleAddPlan = async (workspaceId: string, planData: any) => {
    try {
      await addPlanToWorkspace(workspaceId, planData);
      // Refresh user data or show success notification
    } catch (error) {
      console.error("Failed to add plan:", error);
      // Show error notification
    }
  };

  const handleAddPlanSubmit = async (workspaceId: string, planData: any) => {
    try {
      await handleAddPlan(workspaceId, planData);
      setAddPlanModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error adding plan:", error);
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>User Type</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user._id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.organization?.name || "-"}</TableCell>
                <TableCell>
                  <Chip
                    label={user.isAppAdmin ? "Admin" : "User"}
                    color="default"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      user.role === "admin"
                        ? "Trial Acccess User"
                        : user.ownedWorkspaces?.some((ws) => ws.plan)
                        ? "Premium"
                        : "Free"
                    }
                    color={
                      user.role === "admin"
                        ? "warning"
                        : user.ownedWorkspaces?.some((ws) => ws.plan)
                        ? "success"
                        : "default"
                    }
                  />
                  {user.role === "admin" &&
                    user.ownedWorkspaces?.some((ws) => ws.plan) && (
                      <Chip
                        label={"Premium"}
                        color={"success"}
                        sx={{ ml: 1 }}
                      />
                    )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit User">
                    <IconButton onClick={() => onEdit(user)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add Plan to Workspace">
                    <IconButton onClick={() => handleAddPlanClick(user)}>
                      <Add color="primary" />
                    </IconButton>
                  </Tooltip>

                  {user.role !== "admin" &&
                  !user.ownedWorkspaces?.some((ws) => ws.plan) ? (
                    <Tooltip title="Grant Full Access">
                      <IconButton onClick={() => onGrantAccess(user)}>
                        <LockOpen color="primary" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip title="Manage Workspace Limits">
                        <IconButton
                          onClick={() => {
                            setSelectedUser(user);
                            setLimitsModalOpen(true);
                          }}
                        >
                          <Settings color="primary" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {user.role === "admin" && (
                    <Tooltip title="Revoke Full Access">
                      <IconButton onClick={() => onRevokeAccess(user)}>
                        <Lock color="error" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedUser && (
        <WorkspaceLimitsModal
          workspaces={selectedUser.ownedWorkspaces || []}
          open={limitsModalOpen}
          onClose={() => {
            setLimitsModalOpen(false);
            setSelectedUser(null);
          }}
          onSubmit={async (workspaceId, data) => {
            await updateWorkspacePlanAndCredits(workspaceId, data);
            // Optionally refresh data here
          }}
        />
      )}

      {selectedUser && (
        <AddPlanModal
          open={addPlanModalOpen}
          onClose={() => setAddPlanModalOpen(false)}
          onSubmit={handleAddPlanSubmit}
          workspaces={selectedUser.ownedWorkspaces || []}
        />
      )}
    </>
  );
};
